<template>
  <div class="dispensing-page">
    <div class="page-top">
      <div>
        <h2 class="page-title">出库放粮</h2>
        <p class="page-desc">选择值仓任务与发粮口，下发载重并监控放粮过程</p>
      </div>
      <el-button icon="el-icon-refresh" @click="loadDuties">刷新任务</el-button>
    </div>

    <div class="page-body">
      <aside class="duty-panel">
        <div class="panel-title">今日值仓任务</div>
        <div v-if="loading.duties" class="empty-tip">加载中...</div>
        <div v-else-if="duties.length === 0" class="empty-tip">暂无值仓任务</div>
        <div
          v-for="duty in duties"
          :key="duty.id"
          class="duty-item"
          :class="{ active: selectedDuty && selectedDuty.id === duty.id }"
          @click="selectDuty(duty)"
        >
          <div class="duty-plate">{{ duty.plateNumber || '-' }}</div>
          <div class="duty-meta">{{ duty.warehouse }} · {{ duty.grainType }}</div>
          <div class="duty-weight">载重 {{ duty.loadWeight || '-' }} 吨</div>
        </div>
      </aside>

      <section class="control-panel">
        <template v-if="!selectedDuty">
          <div class="empty-tip large">请从左侧选择值仓任务</div>
        </template>
        <template v-else>
          <div class="info-card">
            <div class="info-row"><span>车牌</span><strong>{{ selectedDuty.plateNumber }}</strong></div>
            <div class="info-row"><span>仓房</span><strong>{{ selectedDuty.warehouse }}</strong></div>
            <div class="info-row"><span>品种</span><strong>{{ selectedDuty.grainType }}</strong></div>
            <div class="info-row"><span>载重</span><strong>{{ selectedDuty.loadWeight }} 吨</strong></div>
          </div>

          <div class="section-block">
            <div class="section-title">选择发粮口</div>
            <el-radio-group v-model="selectedOutletId" @change="onOutletChange">
              <el-radio
                v-for="outlet in outlets"
                :key="outlet.id"
                :label="outlet.id"
                :disabled="!outlet.configComplete"
              >
                {{ outlet.name }}（{{ outlet.configComplete ? '已配置' : '未配置' }}）
              </el-radio>
            </el-radio-group>
          </div>

          <div v-if="preview" class="preview-card">
            <div class="preview-row">
              <span>下发载重</span><strong>{{ preview.loadWeight }} 吨</strong>
            </div>
            <div class="preview-row">
              <span>容重</span>
              <strong>
                {{ preview.bulkDensity || '-' }}
                <template v-if="preview.bulkDensitySource">
                  （{{ bulkDensitySourceText(preview.bulkDensitySource) }}）
                </template>
              </strong>
            </div>
            <div v-if="!preview.canStart" class="block-reason">{{ preview.blockReason }}</div>
          </div>

          <div v-if="plcStatus" class="status-card">
            <div class="status-tags">
              <el-tag :type="plcStatus.online ? 'success' : 'danger'" size="small">
                {{ plcStatus.online ? 'PLC 在线' : 'PLC 离线' }}
              </el-tag>
              <el-tag :type="plcStatus.remoteMode ? 'success' : 'warning'" size="small">
                {{ plcStatus.remoteMode ? '远程模式' : '本地模式' }}
              </el-tag>
              <el-tag :type="plcStatus.running ? 'success' : 'info'" size="small">
                {{ plcStatus.running ? '运行中' : '已停止' }}
              </el-tag>
              <el-tag v-if="plcStatus.lackMaterial" type="danger" size="small">缺料</el-tag>
            </div>
            <div class="status-detail">
              设定载重 {{ plcStatus.loadWeightSetpoint }} · 容重 {{ plcStatus.bulkDensity }} · 车次 {{ plcStatus.loadCount }}
            </div>
          </div>

          <div class="action-row">
            <el-button
              type="primary"
              :disabled="!canStart || !!activeSession"
              :loading="loading.start"
              @click="startDispensing"
            >
              开始放粮
            </el-button>
            <el-button
              type="danger"
              :disabled="!activeSession"
              :loading="loading.stop"
              @click="stopDispensing"
            >
              停止放粮
            </el-button>
            <el-button
              :disabled="!selectedOutletId"
              :loading="loading.snapshot"
              @click="takeSnapshot"
            >
              拍照
            </el-button>
          </div>
        </template>
      </section>

      <section class="video-panel">
        <div class="panel-title">实时监控</div>
        <ZlmWebRtcPlayer ref="player" :play-info="playInfo" />
        <div v-if="photos.length" class="photo-list">
          <div v-for="photo in photos" :key="photo.id" class="photo-item">
            <img :src="photo.filePath" alt="抓拍" />
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { warehouseApi, grainOutletApi, warehouseDutyApi, sideWallApi } from '../utils/api'
import { getCurrentUser } from '../utils/auth'
import ZlmWebRtcPlayer from '../components/ZlmWebRtcPlayer.vue'

export default {
  name: 'OutboundDispensing',
  components: { ZlmWebRtcPlayer },
  data() {
    return {
      duties: [],
      warehouses: [],
      outlets: [],
      selectedDuty: null,
      selectedOutletId: null,
      lastOutletId: null,
      preview: null,
      playInfo: null,
      plcStatus: null,
      activeSession: null,
      photos: [],
      pollTimer: null,
      loading: {
        duties: false,
        start: false,
        stop: false,
        snapshot: false
      }
    }
  },
  computed: {
    canStart() {
      return this.preview && this.preview.canStart && this.selectedOutletId
    },
    currentUser() {
      return getCurrentUser()
    }
  },
  mounted() {
    this.init()
  },
  beforeDestroy() {
    this.clearPoll()
    if (this.selectedOutletId) {
      sideWallApi.stopStream(this.selectedOutletId).catch(() => {})
    }
  },
  methods: {
    async init() {
      await Promise.all([this.loadWarehouses(), this.loadDuties()])
    },

    async loadWarehouses() {
      const res = await warehouseApi.getWarehouses()
      this.warehouses = res.data || []
    },

    async loadDuties() {
      try {
        this.loading.duties = true
        const today = new Date().toISOString().slice(0, 10)
        const res = await warehouseDutyApi.list(today, this.currentUser.id)
        this.duties = res.data || []
      } catch (e) {
        this.$message.error('加载值仓任务失败')
      } finally {
        this.loading.duties = false
      }
    },

    async selectDuty(duty) {
      this.selectedDuty = duty
      this.selectedOutletId = null
      this.preview = null
      this.activeSession = null
      this.photos = []
      this.plcStatus = null
      this.clearPoll()
      await this.loadOutletsForDuty(duty)
    },

    async loadOutletsForDuty(duty) {
      const warehouse = this.warehouses.find(w =>
        (w.warehouseName || w.name) === duty.warehouse
        || (w.warehouseNo || w.code) === duty.warehouse)
      if (!warehouse) {
        this.outlets = []
        this.$message.warning('未找到对应仓房，请检查仓房配置')
        return
      }
      const res = await grainOutletApi.getOutletsWithDevices(warehouse.id)
      this.outlets = res.data || []
    },

    async onOutletChange(outletId) {
      const previousOutletId = this.lastOutletId
      this.lastOutletId = outletId
      this.preview = null
      this.plcStatus = null
      this.clearPoll()
      if (this.playInfo && previousOutletId && previousOutletId !== outletId) {
        await sideWallApi.stopStream(previousOutletId).catch(() => {})
      }
      this.playInfo = null
      if (!outletId || !this.selectedDuty) return

      try {
        const previewRes = await sideWallApi.previewParams(this.selectedDuty.id, outletId)
        this.preview = previewRes.data
      } catch (e) {
        this.$message.error(e.message || '预检失败')
      }

      try {
        const streamRes = await sideWallApi.startStream(outletId)
        this.playInfo = streamRes.data
      } catch (e) {
        this.$message.warning(e.message || '视频流启动失败，放粮仍可继续')
      }

      this.pollStatus()
      this.pollTimer = setInterval(() => this.pollStatus(), 3000)
    },

    async pollStatus() {
      if (!this.selectedOutletId) return
      try {
        const params = this.activeSession
          ? { sessionId: this.activeSession.sessionId }
          : { grainOutletId: this.selectedOutletId }
        const res = await sideWallApi.getStatus(params)
        this.plcStatus = res.data
        if (this.activeSession && res.data.sessionStatus
            && res.data.sessionStatus !== 'running') {
          this.activeSession = { ...this.activeSession, status: res.data.sessionStatus }
        }
      } catch (e) {
        // 轮询失败不打断操作
      }
    },

    clearPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },

    async startDispensing() {
      if (!this.selectedDuty || !this.selectedOutletId) return
      try {
        this.loading.start = true
        const res = await sideWallApi.startDispensing({
          warehouseDutyId: this.selectedDuty.id,
          grainOutletId: this.selectedOutletId,
          operatorId: this.currentUser.id
        })
        this.activeSession = res.data
        this.$message.success('放粮已启动')
        await this.pollStatus()
      } catch (e) {
        this.$message.error(e.message || '启动放粮失败')
      } finally {
        this.loading.start = false
      }
    },

    async stopDispensing() {
      if (!this.activeSession) return
      try {
        this.loading.stop = true
        await sideWallApi.stopDispensing(this.activeSession.sessionId)
        this.activeSession = { ...this.activeSession, status: 'stopped_manual' }
        this.$message.success('已发送停止指令')
        await this.pollStatus()
      } catch (e) {
        this.$message.error(e.message || '停止放粮失败')
      } finally {
        this.loading.stop = false
      }
    },

    async takeSnapshot() {
      if (!this.selectedOutletId) return
      try {
        this.loading.snapshot = true
        const formData = new FormData()
        if (this.activeSession) {
          formData.append('sessionId', this.activeSession.sessionId)
        }
        formData.append('warehouseDutyId', this.selectedDuty.id)
        formData.append('operatorId', this.currentUser.id)
        formData.append('source', 'webrtc_canvas')

        if (this.$refs.player) {
          try {
            const blob = await this.$refs.player.captureFrame()
            formData.append('file', blob, 'snapshot.jpg')
          } catch (e) {
            // 无视频帧时走后端 ISAPI 回退
          }
        }

        const res = await sideWallApi.snapshot(this.selectedOutletId, formData)
        if (this.activeSession) {
          await this.loadPhotos()
        } else {
          this.photos.unshift({ id: res.data.photoId, filePath: res.data.fileUrl })
        }
        this.$message.success('拍照成功')
      } catch (e) {
        this.$message.error(e.message || '拍照失败')
      } finally {
        this.loading.snapshot = false
      }
    },

    async loadPhotos() {
      if (!this.activeSession) return
      const res = await sideWallApi.listPhotos(this.activeSession.sessionId)
      this.photos = res.data || []
    },

    bulkDensitySourceText(source) {
      if (source === 'grain_type') return '品种容重'
      if (source === 'last_record') return '上次放粮'
      return source
    }
  }
}
</script>

<style scoped>
.dispensing-page {
  min-height: calc(100vh - 100px);
  padding: 20px;
}

.page-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 16px;
}

.page-title {
  margin: 0 0 8px;
  font-size: 22px;
}

.page-desc {
  margin: 0;
  color: #909399;
}

.page-body {
  display: grid;
  grid-template-columns: 260px 1fr 420px;
  gap: 16px;
  align-items: start;
}

.duty-panel,
.control-panel,
.video-panel {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
}

.panel-title {
  font-weight: 600;
  margin-bottom: 12px;
}

.duty-item {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  cursor: pointer;
}

.duty-item.active {
  border-color: #409eff;
  background: #ecf5ff;
}

.duty-plate {
  font-weight: 600;
}

.duty-meta,
.duty-weight {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.empty-tip {
  color: #909399;
  text-align: center;
  padding: 24px 0;
}

.empty-tip.large {
  padding: 80px 0;
}

.info-card,
.preview-card,
.status-card {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

.info-row,
.preview-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.section-block {
  margin-bottom: 16px;
}

.section-title {
  font-weight: 600;
  margin-bottom: 8px;
}

.block-reason {
  color: #f56c6c;
  margin-top: 8px;
}

.status-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.status-detail {
  font-size: 13px;
  color: #606266;
}

.action-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.photo-list {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
}

.photo-item img {
  width: 96px;
  height: 72px;
  object-fit: cover;
  border-radius: 4px;
}
</style>
